import { type ReactNode, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, UserRound, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/states';
import { useAuth } from '../../features/auth/auth-context';
import { dashboardApi, dashboardKeys } from '../../features/dashboard/api';
import { CreateTeamReportDialog } from '../../features/team-reports/components/create-team-report-dialog';
import { TeamReportSection } from '../../features/team-reports/components/team-report-list';
import {
  TeamReportActingBanner,
  TeamReportDelegationPanel,
  useTeamReportDelegation,
} from '../../features/team-reports/components/team-report-delegation';

export const Route = createFileRoute('/_app/team-reports/')({
  component: TeamReportsPage,
});

/**
 * Báo cáo công việc của team — APM-TWR-e, plan §7.
 *
 * Không gắn quyền ở route, cùng lý lẽ với `/performance`: thành viên cũng có
 * mục "Báo cáo về tôi" ở đây (TWR-f), nên chặn cả trang là ẩn nó khỏi đúng
 * những người được báo cáo về. Quyền thật nằm ở server — tạo báo cáo khi không
 * có cấp dưới trực tiếp là 403.
 *
 * Mục "Báo cáo tôi tạo" và nút Tạo chỉ hiện khi `hasDirectReports`: một nút
 * chắc chắn nhận 403 không phải một lựa chọn.
 *
 * ## Ba mục, ba cách hiện — APM-TWR-f
 *
 * - **Báo cáo tôi tạo**: leader, luôn hiện (kể cả trống — nó mời bấm "Tạo").
 * - **Team tôi quản lý gián tiếp**: chỉ khi có bản đã chốt nào. Không quyền nào
 *   nói trước được "cấp dưới của tôi có ai là leader không", và một mục trống
 *   thường trực trên trang của mọi leader là nhiễu.
 * - **Báo cáo về tôi**: với người không có cấp dưới, đây là mục **duy nhất** —
 *   luôn hiện, và trống thì giải thích vì sao. Với leader thì chỉ khi có.
 */
function TeamReportsPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [creating, setCreating] = useState<'own' | 'onBehalf' | null>(null);
  const delegation = useTeamReportDelegation();
  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
  });

  const leads = abilities.data?.hasDirectReports ?? false;
  /** Đang làm thay cho một leader (APM-TWRD-d) — thấy mục "của tôi" và nút tạo thay. */
  const incoming = delegation.data?.incoming ?? null;

  return (
    <div className="flex flex-col gap-6">
      {/* `data-tour` — chặng "Tóm tắt công việc của team" của tour AI (APM-TOUR-ai). */}
      <header data-tour="team-reports" className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-medium">{t('teamReport.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('teamReport.subtitle')}</p>
        </div>
        {leads && user ? (
          <Button onClick={() => setCreating('own')}>
            <Plus aria-hidden />
            {t('teamReport.create.open')}
          </Button>
        ) : null}
      </header>

      {/*
        Ủy quyền — APM-TWRD-d. Người làm thay thấy dải báo **trên** danh sách:
        việc họ đang làm cho ai, tới bao giờ, AI của ai. Leader thấy khối ủy
        quyền dưới cùng — việc cấu hình, không phải việc hằng ngày.
      */}
      {incoming ? (
        <TeamReportActingBanner
          incoming={incoming}
          aiEnabled={delegation.data?.incomingAi?.enabled ?? false}
          onCreate={() => setCreating('onBehalf')}
        />
      ) : null}

      {abilities.isPending ? (
        <Skeleton className="h-24" />
      ) : (
        <>
          {/*
            "Của tôi" gồm cả bản làm thay (server gộp). Người từng làm thay mà
            không còn ủy quyền, không có team, thì chỉ hiện khi có bản nào.
          */}
          <TeamReportSection
            scope="mine"
            icon={FileText}
            title={t('teamReport.sections.mine')}
            emptyTitle={t('teamReport.list.emptyMine')}
            emptyDescription={t('teamReport.list.emptyMineHelp')}
            hideWhenEmpty={!leads && !incoming}
          />
          <TeamReportSection
            scope="managed"
            icon={Users}
            title={t('teamReport.sections.managed')}
            hideWhenEmpty
          />
          <TeamReportSection
            scope="aboutMe"
            icon={UserRound}
            title={t('teamReport.sections.aboutMe')}
            emptyTitle={t('teamReport.list.emptyAboutMe')}
            emptyDescription={t('teamReport.list.emptyAboutMeHelp')}
            hideWhenEmpty={leads}
          />
        </>
      )}

      {leads && user ? <TeamReportDelegationPanel managerId={user.id} /> : null}

      {creating === 'own' && user ? (
        <CreateTeamReportDialog managerId={user.id} onClose={() => setCreating(null)} />
      ) : null}
      {creating === 'onBehalf' && incoming ? (
        <CreateTeamReportDialog
          managerId={incoming.managerId}
          onBehalfOf={incoming.managerName}
          onClose={() => setCreating(null)}
        />
      ) : null}
    </div>
  );
}
